import * as React from 'react';
import Spacing from '@able/react/dist/Spacing';
import { IProductOrderTab } from '@applications/order-tracker/config/order-tracker-new-common/types';
import StaticData, { StaticDataGroup } from 'ted-ui/build/StaticData';
import { ProductSheetProductDetails, VariantProgressMapping } from '@applications/order-tracker/config/order-tracker-new-common/constants';
import { ProductDetailsSection } from '../product-details-section';
import JeopardyActionMessage from '../jeopardy-action-message';
import { productDetailsBusinessRules } from '../../../../../../config/order-tracker-new-common/order-products-utils';
import ActionButton from '@able/react/dist/ActionButton';
import { Application } from '@applications/order-tracker';
import { Components, Links } from '@telstra-connect/order-tracker/activity-tracking';
import { useResize } from 'ted-ui/build/util';

const trackOrderEnquiryLinkClick = (): void => {
  Application.tracker.trackLink(
    Links.productDetails.OrderEnquiryOpensNewTab,
    Components.productDetailsPage
  );
};

const ProductDetails = ({ product }: IProductOrderTab) => {
  const {
    showProjectManagerDetails,
    showTelstraConsultantContact,
    showBillingAccountNumber,
    showEstimatedCompletion,
  } = productDetailsBusinessRules(product);
  const [columnSize, setColumnSize] = React.useState(2);

  useResize((size) => {
    if (size === 'xs') {
      setColumnSize(1);
    } else {
      setColumnSize(2);
    }
  }, []);

  const renderLozenge = (label: string) => {
    const normalizedProgress = label.toLowerCase();
    return (
      <StaticData
        lozenge={{
          label,
          variant: VariantProgressMapping[normalizedProgress]
        }}
        name={ProductSheetProductDetails.ProductProgress}
      />
    )
  }
  const renderProjectManagerDetails = () => {
    if (showProjectManagerDetails()) {
      return (
        <StaticData
          name={ProductSheetProductDetails.TelstraOrderContact}
          value={[
            product?.projectManagerContact.name,
            product?.projectManagerContact.email
          ]}
        />
      );
    } else {
      return null;
    }
  };
  const renderTelstraConsultantContact = () => {
    if (showTelstraConsultantContact()) {
      return (
        <>
        <StaticData
          name={ProductSheetProductDetails.TelstraOrderContact}
          value={[
            product?.telstraConsultantContact.name
          ]}
        />
        <dd>
        <ActionButton
          id="order-enquiry-action-button"
          element="Link"
          opensNewTab
          label={ProductSheetProductDetails.ActionButtonLabel}
          variant="LowEmphasis"
          events={{ onClick: () => trackOrderEnquiryLinkClick() }}
          to={ProductSheetProductDetails.OrderEnquiry}
        />
        </dd>
        </>
      );
    } else {
      return null;
    }
  };
  const renderTelstraOrderContact = () => {
    const projectManagerDetails = renderProjectManagerDetails();
    const telstraConsultantContactDetails = renderTelstraConsultantContact();
    if (projectManagerDetails !== null) {
      return projectManagerDetails;
    } else if (telstraConsultantContactDetails !== null) {
      return telstraConsultantContactDetails;
    } else {
      return (
        <StaticData
          name={ProductSheetProductDetails.TelstraOrderContact}
          value={ProductSheetProductDetails.WaitingForInformation}
        />
      )
    }
  }

  const renderProductDetailsWrapper = (): React.ReactNode => (
    <React.Fragment>
      {renderJeopardyActionMessage()}
      {renderAllProductDetailsSections()}
    </React.Fragment>
  );

  const renderJeopardyActionMessage = (): React.ReactNode => (
    <JeopardyActionMessage activity={product?.activity} />
  );

  const renderAllProductDetailsSections = (): React.ReactNode =>
  (
    <StaticDataGroup  config={{
      horizontalColumn: columnSize
    }}
    id="product-details-group-data">
          {product?.productProgress && renderLozenge(product.productProgress)}
          {showEstimatedCompletion() &&
            <StaticData
              id="static-data-test"
              name={ProductSheetProductDetails.EstimatedCompletion}
              value={product?.estimatedCompletionDate}
            />
          }
          {renderTelstraOrderContact()}
            <StaticData
              id="static-data-test"
              name={ProductSheetProductDetails.ActivityType}
              value={product?.activityType}
            />
            {showBillingAccountNumber() &&
              <StaticData
                id="static-data-test"
                name={ProductSheetProductDetails.BillingAccount}
                value={product?.billingAccountNumber}
              />
            }
            <StaticData
              id="static-data-test"
              name={ProductSheetProductDetails.OrderNumber}
              value={product?.orderNumber}
            />
    </StaticDataGroup>
  );

  return (
    <Spacing top="spacing3x">
      <ProductDetailsSection
        title={ProductSheetProductDetails.Title}
        descriptionGroup={renderProductDetailsWrapper()}
      />
    </Spacing>
  );
};

export default ProductDetails;
